import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import LinkIcon from '@mui/icons-material/Link';
import {
  APPLICANT_STATUS_COLORS,
  INTERVIEW_STATUS_COLORS,
  OFFER_STATUS_COLORS,
} from '../../../../constants/hrm';
import {
  useApplicant, useUpdateApplicantStatus,
} from '../../../../hooks/useHRM';
import { hasPermission } from '../../../../utils/permissionUtils';

const PIPELINE_STEPS = ['APPLIED', 'SCREENING', 'SHORTLISTED', 'INTERVIEW', 'OFFERED', 'HIRED'];

const ApplicantDetail = () => {
  const { chatbotId, applicantId } = useParams();
  const navigate = useNavigate();

  const [error, setError] = useState(null);

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  const { data: applicantData, isLoading: loading, refetch: refetchApplicant } = useApplicant(chatbotId, applicantId, { enabled: !!chatbotId && !!applicantId && canView });

  const applicant = applicantData?.data || null;
  const interviews = applicant?.interviews || [];
  const offers = applicant?.offers || [];

  const updateApplicantStatus = useUpdateApplicantStatus(chatbotId);
  const updatingStatus = updateApplicantStatus.isPending;

  const handleStatusUpdate = async (newStatus) => {
    try {
      await updateApplicantStatus.mutateAsync({ id: Number(applicantId), status: newStatus });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update applicant status');
    }
  };

  const getActiveStep = () => {
    if (!applicant) return 0;
    if (applicant.status === 'REJECTED') return -1;
    const idx = PIPELINE_STEPS.indexOf(applicant.status);
    return idx >= 0 ? idx : 0;
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view the HRM module.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !applicant) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
          Go Back
        </Button>
      </Box>
    );
  }

  const activeStep = getActiveStep();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', mb: 3, gap: 2 }}>
        <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/hrm/applicants`)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {applicant?.first_name} {applicant?.last_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={applicant?.status}
              size="small"
              sx={{
                backgroundColor: APPLICANT_STATUS_COLORS[applicant?.status] || '#9e9e9e',
                color: '#fff',
                fontWeight: 'bold',
              }}
            />
            {applicant?.job_title && (
              <Typography variant="body2" color="text.secondary">
                Applied for: {applicant.job_title}
              </Typography>
            )}
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => refetchApplicant()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Pipeline Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Recruitment Pipeline
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {applicant?.status === 'REJECTED' ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Chip
              label="REJECTED"
              sx={{
                backgroundColor: '#f44336',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '1rem',
                py: 2,
                px: 3,
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This applicant has been rejected from the pipeline.
            </Typography>
          </Box>
        ) : (
          <Stepper activeStep={activeStep} alternativeLabel>
            {PIPELINE_STEPS.map((step, index) => (
              <Step key={step} completed={index < activeStep}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontWeight: index === activeStep ? 'bold' : 'normal',
                    },
                  }}
                >
                  {step}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>

      {/* Quick Status Update Buttons */}
      {canModify && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Quick Status Update
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {PIPELINE_STEPS.filter((s) => s !== applicant?.status).map((status) => (
              <Button
                key={status}
                variant="outlined"
                size="small"
                disabled={updatingStatus}
                onClick={() => handleStatusUpdate(status)}
                sx={{
                  borderColor: APPLICANT_STATUS_COLORS[status],
                  color: APPLICANT_STATUS_COLORS[status],
                  '&:hover': {
                    backgroundColor: `${APPLICANT_STATUS_COLORS[status]}10`,
                    borderColor: APPLICANT_STATUS_COLORS[status],
                  },
                }}
              >
                {status}
              </Button>
            ))}
            {applicant?.status !== 'REJECTED' && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                disabled={updatingStatus}
                onClick={() => handleStatusUpdate('REJECTED')}
              >
                REJECT
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Personal Info & Application Details */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Personal Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{applicant?.email || '-'}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{applicant?.phone || '-'}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Current Company</Typography>
                  <Typography variant="body1">{applicant?.current_company || '-'}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WorkHistoryIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Experience</Typography>
                  <Typography variant="body1">
                    {applicant?.experience_years != null ? `${applicant.experience_years} years` : '-'}
                  </Typography>
                </Box>
              </Box>
              {applicant?.resume_url && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinkIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Resume</Typography>
                    <Typography
                      variant="body1"
                      component="a"
                      href={applicant.resume_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'primary.main', textDecoration: 'underline' }}
                    >
                      View Resume
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Application Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Job Applied</Typography>
                <Typography variant="body1">{applicant?.job_title || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Source</Typography>
                <Typography variant="body1">{applicant?.source || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Applied Date</Typography>
                <Typography variant="body1">
                  {applicant?.applied_date
                    ? new Date(applicant.applied_date).toLocaleDateString()
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Score</Typography>
                <Typography variant="body1">
                  {applicant?.score != null ? applicant.score : 'Not scored'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Expected Salary</Typography>
                <Typography variant="body1">
                  {applicant?.expected_salary
                    ? applicant.expected_salary.toLocaleString()
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Interviews */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Interviews ({interviews.length})
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {interviews.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No interviews scheduled for this applicant.
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Interviewer</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Feedback</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {interviews.map((interview) => (
                  <TableRow key={interview.id}>
                    <TableCell>
                      {interview.interview_date
                        ? new Date(interview.interview_date).toLocaleString()
                        : '-'}
                    </TableCell>
                    <TableCell>{interview.interview_type || '-'}</TableCell>
                    <TableCell>{interview.interviewer_name || '-'}</TableCell>
                    <TableCell>
                      {interview.duration_minutes ? `${interview.duration_minutes} min` : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={interview.status}
                        size="small"
                        color={INTERVIEW_STATUS_COLORS[interview.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell>{interview.score != null ? interview.score : '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {interview.feedback || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Offers */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Offers ({offers.length})
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {offers.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No offers created for this applicant.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {offers.map((offer) => (
              <Grid item xs={12} md={6} key={offer.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {offer.job_title || 'Offer'}
                      </Typography>
                      <Chip
                        label={offer.status}
                        size="small"
                        color={OFFER_STATUS_COLORS[offer.status] || 'default'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Salary: {offer.salary_offered ? offer.salary_offered.toLocaleString() : '-'}
                    </Typography>
                    {offer.bonus && (
                      <Typography variant="body2" color="text.secondary">
                        Bonus: {offer.bonus.toLocaleString()}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Start Date: {offer.start_date ? new Date(offer.start_date).toLocaleDateString() : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Expiry: {offer.expiry_date ? new Date(offer.expiry_date).toLocaleDateString() : '-'}
                    </Typography>
                    {offer.benefits && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Benefits: {offer.benefits}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default ApplicantDetail;
